import { Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconFileTypePdf } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { FilePreviewButton, FilePreviewModal, useAppNotification } from '@feature/utils';

import { useTravelRequestDocumentBlob, useTravelRequestDownloadDocument } from '../model/TravelRequestQuery';
import { TravelRequest } from '../model/TravelRequestTypes';

const DOCUMENT_CONTENT_TYPE = 'application/pdf';

interface TravelRequestDocumentButtonProps {
  request: TravelRequest;
}

export function TravelRequestDocumentButton({ request }: TravelRequestDocumentButtonProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const download = useTravelRequestDownloadDocument(request.id);
  const documentBlob = useTravelRequestDocumentBlob(request.id);
  const [previewOpened, { open: openPreview, close: closePreview }] = useDisclosure(false);
  const { available, fileName, formCode } = request.document;
  const filename = fileName ?? `${formCode}.pdf`;

  const onDownload = () => download.mutate(filename, { onError: notification.error });

  return (
    <>
      <FilePreviewButton
        contentType={DOCUMENT_CONTENT_TYPE}
        disabled={!available}
        onClick={openPreview}
      />
      <Button
        size="xs"
        variant="outline"
        leftSection={<IconFileTypePdf size={14} />}
        disabled={!available}
        loading={download.isPending}
        onClick={onDownload}
      >
        {t('travel.requests.view.downloadDocument')}
      </Button>
      {previewOpened && (
        <FilePreviewModal
          title={filename}
          contentType={DOCUMENT_CONTENT_TYPE}
          fetchBlob={() => documentBlob.mutateAsync()}
          onClose={closePreview}
          onDownload={onDownload}
          downloading={download.isPending}
        />
      )}
    </>
  );
}
